// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import type { FunctionComponent } from "react";
import type { SubGroupsAndAsset } from "@/types/view/project";
import Err from "../common/Err";
import { Skeleton } from "../ui/skeleton";
import AssetRow from "./AssetRow";
import ProjectRow from "./ProjectRow";
import { checkType } from "./utils";

interface Props {
  items?: SubGroupsAndAsset[];
  onFetchData: (projectSlug: string, projectId: string) => any;
  error?: Error;
  isLoading?: boolean;
  parentProjectSlug: string;
  compact?: boolean;
  Empty?: React.ReactNode;
}

const NestedRowSkeleton: FunctionComponent = () => (
  <div className="flex flex-row items-center gap-3 px-3 py-2">
    <Skeleton className="h-8 w-8 rounded-lg shrink-0" />
    <div className="flex flex-1 flex-col gap-1.5">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  </div>
);

const NestedList: FunctionComponent<Props> = ({
  items,
  onFetchData,
  error,
  isLoading = false,
  parentProjectSlug,
  Empty = null,
}) => {
  if (isLoading && !items) {
    return (
      <>
        <NestedRowSkeleton />
        <NestedRowSkeleton />
        <NestedRowSkeleton />
      </>
    );
  }

  if (error) {
    return <Err />;
  }

  if (!items || items.length === 0) {
    return Empty;
  }

  return (
    <>
      {items.map((item, index) => {
        const { asset, subgroup } = checkType(item);
        if (asset) {
          return (
            <AssetRow
              key={asset.id}
              asset={asset}
              projectSlug={parentProjectSlug}
              variant={"nested"}
              isLast={index === items.length - 1}
            />
          );
        }
        return (
          <ProjectRow
            key={subgroup.id}
            project={subgroup}
            subgroupsWithAssets={
              subgroup.subGroupsAndAsset as SubGroupsAndAsset[]
            }
            onFetchData={onFetchData}
            depth="nested"
            isLast={index === items.length - 1}
          />
        );
      })}
    </>
  );
};

export default NestedList;
